/**
 * When a founder and an investor may open a conversation.
 *
 * A near-leaf module: its only runtime import is `@/lib/commerce/copy`, itself
 * leaf (Platform.OS only) — so both the UI and the tests can use this without
 * dragging in the Supabase client. The SERVER-side twin of this rule lives in
 * the `conversations` RLS INSERT policy — this is the affordance, that is the
 * enforcement. A hidden button is not a permission.
 */

import type { DealStage } from '@/lib/dealflow/stage';
import { COPY } from '@/lib/commerce/copy';

/**
 * The spine's stages. Type-only import: erased at compile time, so the
 * no-imports property above is intact — and `stage.ts` is itself zero-import,
 * so nothing would be pulled in either way.
 *
 * An ALIAS rather than a re-declaration of the same literals. Two independent
 * copies of one union that TypeScript cannot keep in step is precisely the
 * drift the stage spine exists to remove; with the alias, divergence is a
 * compile error. (If `stage.ts` ever gains a runtime import this must be
 * revisited — which is why it documents its zero-import property as
 * load-bearing.)
 */
export type DealflowStatus = DealStage;

export interface ConversationEligibility {
  /** The investor's pipeline label for this startup, or null if not in it. */
  dealflowStatus: DealflowStatus | null;
  /**
   * Whether the FOUNDER has granted this investor data-room access.
   *
   * The founder's row in `data_room_access`, NOT anything derived from the
   * stage. The two are not the same fact: `respond_to_data_room_request` writes
   * both together, and since 20260806090000 `conclude_dd_report` revokes on
   * `pass`/`monitor` — but NOT on `invest`, so an `invested` deal carries a live
   * `granted` row by design. A `rejected` one still can too: a founder may grant
   * access directly, and a grant made outside a diligence is never concluded.
   * Door A clause 2 of the conversations INSERT policy reads that row, and this
   * boolean shadows that clause.
   */
  dataRoomAccessGranted: boolean;
}

/**
 * Stages at which a channel is open.
 *
 * Deliberately excludes 'screening'. That means the startup is merely PRESENT
 * in someone's pipeline, which is now a very weak statement:
 * `investor-analyze-deck` upserts a dealflow row with status 'screening' for
 * every deck an investor analyses. Opening a channel on that would tell a
 * founder someone is interested because a PDF was opened.
 *
 * 'rejected' is excluded too — a rejection should not be a doorway to first
 * contact. (It does not close a conversation that already exists; that is a
 * separate question, handled where a thread is archived.)
 */
const OPEN_STAGES: ReadonlySet<DealflowStatus> = new Set<DealflowStatus>([
  'interested',
  // Asked for the data room — a stronger signal than `interested`, and the
  // founder is being asked to act, so a channel is warranted while they decide.
  'awaiting_access',
  'diligence',
  'invested',
  // Monitoring means the investor deliberately chose to keep following this
  // company — warmer than `interested`, so closing the channel here would be
  // backwards. `rejected` stays closed.
  'monitoring',
]);

export function canOpenConversation(eligibility: ConversationEligibility): boolean {
  // The founder's own grant outranks any pipeline label. If they have handed
  // this investor their data room, a channel is warranted regardless of how the
  // investor has filed them — including after a pass, which must not become a
  // way to silence a founder you already took documents from.
  if (eligibility.dataRoomAccessGranted) return true;

  // Unknown/new enum values land here and are refused: adding a stage must be
  // a deliberate decision about whether it opens a channel, not an accident.
  return (
    eligibility.dealflowStatus !== null &&
    OPEN_STAGES.has(eligibility.dealflowStatus)
  );
}

/** Why the channel is closed, for UI copy. Null when it is open. */
export function conversationClosedReason(
  eligibility: ConversationEligibility,
): string | null {
  if (canOpenConversation(eligibility)) return null;
  if (eligibility.dealflowStatus === 'rejected') return 'You passed on this startup.';
  if (eligibility.dealflowStatus === null) return 'Add this startup to your pipeline first.';
  return 'Mark this startup as interested to message the founder.';
}

// ---------------------------------------------------------------------------
// DOOR B — founder cold outreach.
//
// The product `direct_messaging` actually sells: a founder on a paid tier
// contacting an investor they have no prior relationship with. Distinct from
// Door A, which is a relationship the investor themselves initiated.
//
// Two independent gates, and both must pass.
// ---------------------------------------------------------------------------

export interface ColdOutreachEligibility {
  /** Founder's plan grants outreach (`direct_messaging` allowance remaining). */
  founderCanInitiate: boolean;
  /** The investor's own setting. Not purchasable. */
  investorAcceptsColdOutreach: boolean;
}

export function canColdOutreach(eligibility: ColdOutreachEligibility): boolean {
  return eligibility.founderCanInitiate && eligibility.investorAcceptsColdOutreach;
}

/**
 * Why outreach is refused, for UI copy. Null when it is allowed.
 *
 * The investor's choice is reported FIRST when both gates block. Telling a
 * founder to upgrade when the investor is closed anyway sells them something
 * that cannot work — and an investor's preference is not a paywall to be
 * bought past.
 */
export function coldOutreachClosedReason(
  eligibility: ColdOutreachEligibility,
): string | null {
  if (canColdOutreach(eligibility)) return null;
  if (!eligibility.investorAcceptsColdOutreach) {
    return 'This investor is not accepting direct outreach.';
  }
  return COPY.coldOutreachPlanReason;
}
